// This script repairs all user badges by checking user activities against badge criteria
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

// Helper type to handle badge responses from RPC functions
type BadgeResponse = {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  created_at?: string;
};

// Type for profile with post counts
type ProfileWithCounts = {
  id: string;
  username: string;
  post_count: number;
  comment_count: number;
  follower_count: number;
  following_count: number;
  like_count: number;
};

/**
 * Gets a badge by name, ensures it exists
 */
async function ensureBadgeExists(
  badgeName: string, 
  description: string, 
  icon: string, 
  color: string
): Promise<string | null> {
  console.log(`Ensuring ${badgeName} badge exists...`);
  
  try {
    // 1. Get the badge details (if it exists)
    const { data: badgeData, error: badgeError } = await supabase
      .rpc('get_badge_by_name', { badge_name: badgeName });
    
    if (badgeError) {
      console.error(`Error checking ${badgeName} badge:`, badgeError);
      return null;
    }
    
    // If badge exists, return its ID
    if (badgeData) {
      const badge = badgeData as unknown as BadgeResponse;
      if (badge && badge.id) {
        console.log(`Found existing ${badgeName} badge with ID:`, badge.id);
        return badge.id;
      }
    }
    
    // If not found, ensure it exists through the trusted server API
    const { data: newBadgeId, error: createError } = await supabase
      .rpc('ensure_badge_exists', {
        badge_name: badgeName,
        badge_description: description,
        badge_icon: icon,
        badge_color: color
      });
    
    if (createError) {
      console.error(`Error ensuring ${badgeName} badge exists:`, createError);
      return null;
    }
    
    console.log(`Ensured ${badgeName} badge exists with ID:`, newBadgeId);
    return newBadgeId as string;
  } catch (error) {
    console.error(`Unexpected error ensuring ${badgeName} badge exists:`, error);
    return null;
  }
}

/**
 * Awards a badge to a user
 */
async function awardBadgeSafe(
  userId: string, 
  badgeId: string, 
  progressCurrent: number = 100, 
  progressTotal: number = 100
): Promise<boolean> {
  console.log(`Awarding badge ${badgeId} to user ${userId} safely...`);
  
  try {
    // Check if the user already has this badge
    const { data: hasAlready, error: checkError } = await supabase
      .rpc('user_has_badge', { 
        user_id_param: userId, 
        badge_id_param: badgeId 
      });
    
    if (checkError) {
      console.error('Error checking if user has badge:', checkError);
      return false;
    }
    
    // If user already has the badge, we're done
    if (hasAlready) {
      console.log(`User ${userId} already has badge ${badgeId}`);
      return true;
    }
    
    // Award the badge through a trusted server function
    const { data: success, error: awardError } = await supabase
      .rpc('award_badge_to_user', {
        user_id_param: userId,
        badge_id_param: badgeId,
        progress_current_param: progressCurrent,
        progress_total_param: progressTotal
      });
    
    if (awardError) {
      console.error('Error safely awarding badge:', awardError);
      return false;
    }
    
    console.log(`Successfully awarded badge ${badgeId} to user ${userId}`);
    return true;
  } catch (error) {
    console.error('Unexpected error awarding badge safely:', error);
    return false;
  }
}

/**
 * Fetches all users with their activity counts
 */
async function getAllUsersWithCounts(): Promise<ProfileWithCounts[]> {
  try {
    // Get all profiles with post counts
    const { data: profilesWithPostCounts, error: postError } = await supabase
      .rpc('get_profiles_with_activity_counts');
    
    if (postError) {
      console.error('Error fetching profiles with counts:', postError);
      return [];
    }
    
    return profilesWithPostCounts as ProfileWithCounts[];
  } catch (error) {
    console.error('Error getting profiles with counts:', error);
    return [];
  }
}

/**
 * Main repair function that checks and awards all relevant badges
 */
export async function repairAllUserBadges(): Promise<boolean> {
  console.log('Starting badge repair for all users...');
  
  try {
    // 1. Get all badge IDs we need
    const newUserBadgeId = await ensureBadgeExists(
      'New User', 
      'Awarded to new members when they join Vibebops!',
      '🎉',
      'bg-emerald-500'
    );
    
    const firstPostBadgeId = await ensureBadgeExists(
      'First Post', 
      'Awarded for creating your first post',
      '📝',
      'bg-blue-500'
    );
    
    const creativeSpark = await ensureBadgeExists(
      'Creative Spark', 
      'For uploading 5 unique posts',
      '✨',
      'bg-violet-500'
    );
    
    const friendlyFace = await ensureBadgeExists(
      'Friendly Face', 
      'For receiving 5+ positive reactions (likes, upvotes)',
      '😊',
      'bg-yellow-500'
    );
    
    const topCommenter = await ensureBadgeExists(
      'Top Commenter', 
      'For leaving 25+ comments',
      '💬',
      'bg-indigo-500'
    );
    
    const connector = await ensureBadgeExists(
      'Connector', 
      'For gaining 10+ followers or following 20+ users',
      '🔗',
      'bg-cyan-500'
    );
    
    // Check if any badge IDs failed
    if (!newUserBadgeId || !firstPostBadgeId || !creativeSpark || 
        !friendlyFace || !topCommenter || !connector) {
      console.error('Failed to ensure all necessary badges exist');
      return false;
    }
    
    // 2. Get all users with their activity counts
    const users = await getAllUsersWithCounts();
    
    if (users.length === 0) {
      console.error('No users found to repair badges for');
      return false;
    }
    
    // 3. For each user, check and award badges
    for (const user of users) {
      console.log(`Repairing badges for user ${user.username} (${user.id})`);
      
      // Award New User badge to everyone
      await awardBadgeSafe(user.id, newUserBadgeId);
      
      // First Post badge for users with at least 1 post
      if (user.post_count > 0) {
        await awardBadgeSafe(user.id, firstPostBadgeId);
      }
      
      // Creative Spark badge for users with 5+ posts
      if (user.post_count >= 5) {
        await awardBadgeSafe(user.id, creativeSpark);
      }
      
      // Top Commenter badge for users with 25+ comments
      if (user.comment_count >= 25) {
        await awardBadgeSafe(user.id, topCommenter);
      }
      
      // Connector badge for users with 10+ followers or 20+ following
      if (user.follower_count >= 10 || user.following_count >= 20) {
        await awardBadgeSafe(user.id, connector);
      }
      
      // Friendly Face badge for users with 5+ likes
      if (user.like_count >= 5) {
        await awardBadgeSafe(user.id, friendlyFace);
      }
    }
    
    console.log('Badge repair completed successfully!');
    return true;
  } catch (error) {
    console.error('Error repairing all user badges:', error);
    return false;
  }
}

/**
 * Repairs badges for a single user
 */
export async function repairUserBadges(userId: string): Promise<boolean> {
  console.log(`Repairing badges for user ${userId}...`);
  
  try {
    // 1. Get all badge IDs we need
    const newUserBadgeId = await ensureBadgeExists(
      'New User', 
      'Awarded to new members when they join Vibebops!',
      '🎉',
      'bg-emerald-500'
    );
    
    const firstPostBadgeId = await ensureBadgeExists(
      'First Post', 
      'Awarded for creating your first post',
      '📝',
      'bg-blue-500'
    );
    
    const creativeSpark = await ensureBadgeExists(
      'Creative Spark', 
      'For uploading 5 unique posts',
      '✨',
      'bg-violet-500'
    );
    
    const friendlyFace = await ensureBadgeExists(
      'Friendly Face', 
      'For receiving 5+ positive reactions (likes, upvotes)',
      '😊',
      'bg-yellow-500'
    );
    
    const topCommenter = await ensureBadgeExists(
      'Top Commenter', 
      'For leaving 25+ comments',
      '💬',
      'bg-indigo-500'
    );
    
    const connector = await ensureBadgeExists(
      'Connector', 
      'For gaining 10+ followers or following 20+ users',
      '🔗',
      'bg-cyan-500'
    );
    
    // Check if any badge IDs failed
    if (!newUserBadgeId || !firstPostBadgeId || !creativeSpark || 
        !friendlyFace || !topCommenter || !connector) {
      console.error('Failed to ensure all necessary badges exist');
      return false;
    }
    
    // 2. Get the user's activity counts
    const { data: userData, error: userError } = await supabase
      .rpc('get_user_activity_counts', { user_id_param: userId });
    
    if (userError || !userData) {
      console.error('Error fetching user activity counts:', userError);
      return false;
    }
    
    const user = userData as ProfileWithCounts;
    
    // 3. Check and award badges
    console.log(`Repairing badges for user ${user.username} (${user.id})`);
    
    // Award New User badge
    await awardBadgeSafe(user.id, newUserBadgeId);
    
    // First Post badge for users with at least 1 post
    if (user.post_count > 0) {
      await awardBadgeSafe(user.id, firstPostBadgeId);
    }
    
    // Creative Spark badge for users with 5+ posts
    if (user.post_count >= 5) {
      await awardBadgeSafe(user.id, creativeSpark);
    }
    
    // Top Commenter badge for users with 25+ comments
    if (user.comment_count >= 25) {
      await awardBadgeSafe(user.id, topCommenter);
    }
    
    // Connector badge for users with 10+ followers or 20+ following
    if (user.follower_count >= 10 || user.following_count >= 20) {
      await awardBadgeSafe(user.id, connector);
    }
    
    // Friendly Face badge for users with 5+ likes
    if (user.like_count >= 5) {
      await awardBadgeSafe(user.id, friendlyFace);
    }
    
    console.log(`Badge repair for user ${userId} completed successfully!`);
    return true;
  } catch (error) {
    console.error(`Error repairing badges for user ${userId}:`, error);
    return false;
  }
}

/**
 * Repairs badges for a single user and returns details about changes
 * This version returns information about badges that were newly earned
 * @param userId User ID to check and repair badges for
 * @returns Object with success status and information about newly earned badges
 */
export async function checkAndRepairUserBadges(userId: string): Promise<{
  success: boolean;
  newlyEarnedBadges: {
    name: string;
    icon: string;
    description: string;
  }[];
}> {
  console.log(`Checking badges for user ${userId}...`);
  
  try {
    // First, get the list of badges the user currently has earned
    const { data: currentBadges, error: badgesError } = await supabase
      .from("user_badges")
      .select("badge_id, earned_at")
      .eq("user_id", userId)
      .not("earned_at", "is", null);
      
    if (badgesError) {
      console.error('Error fetching current badges:', badgesError);
      return { success: false, newlyEarnedBadges: [] };
    }
    
    // Get all badge details for lookup
    const { data: allBadges, error: allBadgesError } = await supabase
      .from("badges")
      .select("id, name, icon, description");
      
    if (allBadgesError) {
      console.error('Error fetching all badges:', allBadgesError);
      return { success: false, newlyEarnedBadges: [] };
    }
    
    // Create a set of badge IDs that are already earned
    const earnedBadgeIds = new Set(currentBadges.map(badge => badge.badge_id));
    
    // Run the standard badge repair
    const success = await repairUserBadges(userId);
    
    if (!success) {
      return { success: false, newlyEarnedBadges: [] };
    }
    
    // Get the updated list of badges
    const { data: updatedBadges, error: updatedError } = await supabase
      .from("user_badges")
      .select("badge_id, earned_at")
      .eq("user_id", userId)
      .not("earned_at", "is", null);
      
    if (updatedError) {
      console.error('Error fetching updated badges:', updatedError);
      return { success: true, newlyEarnedBadges: [] }; // Report success because repair happened
    }
    
    // Find newly earned badges (badges that are in updatedBadges but weren't in earnedBadgeIds)
    const newlyEarnedBadgeIds = updatedBadges
      .map(badge => badge.badge_id)
      .filter(badgeId => !earnedBadgeIds.has(badgeId));
    
    // Get details for newly earned badges
    const newlyEarnedBadges = allBadges
      .filter(badge => newlyEarnedBadgeIds.includes(badge.id))
      .map(badge => ({
        name: badge.name,
        icon: badge.icon,
        description: badge.description
      }));
    
    console.log(`Found ${newlyEarnedBadges.length} newly earned badges for user ${userId}`);
    return {
      success: true,
      newlyEarnedBadges
    };
  } catch (error) {
    console.error(`Error checking badges for user ${userId}:`, error);
    return {
      success: false,
      newlyEarnedBadges: []
    };
  }
} 